(function () {
  'use strict';
  if (window.__GLOBAL_GIFT_SYSTEM_INSTALLED__) return;
  window.__GLOBAL_GIFT_SYSTEM_INSTALLED__ = true;

  // Kwoty w groszach. Warianty muszą mieć cenę 0 zł lub aktywny rabat 100%.
  const GIFT_TIERS = [
    { min: 1, max: 9899, variantId: 58092038127880 },
    { min: 9900, max: 19899, variantId: 58092038193416 },
    { min: 19900, max: 28999, variantId: 58092038258952 },
    { min: 29000, max: 49899, variantId: 58092038324488 },
    { min: 49900, max: 99999, variantId: 58092038652168 },
    { min: 100000, max: Infinity, variantId: 58092038717704 }
  ];
  const ROOT = window.Shopify?.routes?.root || '/';
  const WRAP_PRODUCT_ID = 12344141414664;
  const WRAP_VARIANT_ID = 59074678718728;
  const WRAP_LABEL = 'Pakowanie na prezent';
  const nativeFetch = window.fetch.bind(window);
  const pendingPurchases = new WeakMap();
  let queue = Promise.resolve();
  let checkingOut = false;
  let wrappingBusy = false;

  // Zmiany koszyka i checkout używają jednej kolejki. Błąd trafia do
  // wywołującego, ale nie blokuje następnej próby.
  function enqueue(action) {
    const result = queue.then(action);
    queue = result.catch(() => {});
    return result;
  }

  function isGift(item) {
    return String(item.properties?._is_gift) === 'true';
  }

  function isGiftWrap(item) {
    return Number(item.product_id) === WRAP_PRODUCT_ID || Number(item.variant_id) === WRAP_VARIANT_ID;
  }

  function canWrap(item) {
    return !isGift(item) && !isGiftWrap(item);
  }

  function wantedGift(cart) {
    const subtotal = cart.items.filter(canWrap)
      .reduce((sum, item) => sum + item.final_line_price, 0);
    return GIFT_TIERS.find(tier => subtotal >= tier.min && subtotal <= tier.max)?.variantId || null;
  }

  async function requestJson(path, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await nativeFetch(path, {
        ...options,
        cache: 'no-store',
        signal: controller.signal,
        headers: { Accept: 'application/json', ...options.headers }
      });
      const data = await response.json();
      if (!response.ok || data.errors || Number(data.status) >= 400) {
        const message = data.description || data.message || data.errors;
        throw new Error(typeof message === 'string' ? message : 'Nie udało się zaktualizować koszyka.');
      }
      return data;
    } finally {
      clearTimeout(timeout);
    }
  }

  function postJson(path, body) {
    return requestJson(`${ROOT}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  const getCart = () => requestJson(`${ROOT}cart.js`);

  function wrappingPlan(cart) {
    const wanted = new Map();
    for (const item of cart.items.filter(canWrap)) {
      const id = item.properties?._gift_wrap_id;
      if (!id || item.properties?._gift_wrap_selected !== 'true') continue;
      const entry = wanted.get(id) || { quantity: 0, title: item.title };
      entry.quantity += item.quantity;
      wanted.set(id, entry);
    }
    const existing = new Map();
    const removals = {};
    for (const item of cart.items.filter(isGiftWrap)) {
      const id = item.properties?._gift_wrap_parent;
      if (!wanted.has(id) || Number(item.variant_id) !== WRAP_VARIANT_ID) {
        removals[item.key] = 0;
        continue;
      }
      if (!existing.has(id)) existing.set(id, []);
      existing.get(id).push(item);
    }
    const additions = [];
    let change = null;
    for (const [id, parent] of wanted) {
      const lines = existing.get(id) || [];
      if (!lines.length) {
        additions.push({
          id: WRAP_VARIANT_ID,
          quantity: parent.quantity,
          properties: { _gift_wrap_parent: id, 'Dla produktu': parent.title }
        });
      } else if (lines.reduce((sum, item) => sum + item.quantity, 0) !== parent.quantity) {
        // Discounts can split one variant into several lines. Count all of them.
        lines.slice(1).forEach(item => { removals[item.key] = 0; });
        change ||= { id: lines[0].key, quantity: parent.quantity };
      }
    }
    return { removals, additions, change };
  }

  async function reconcileWrapping(cart) {
    let changed = false;
    const limit = cart.items.length * 2 + 6;
    for (let attempt = 0; attempt < limit; attempt++) {
      const plan = wrappingPlan(cart);
      if (Object.keys(plan.removals).length) {
        await postJson('cart/update.js', { updates: plan.removals });
      } else if (plan.change) {
        // change.js validates inventory when increasing an existing add-on.
        await postJson('cart/change.js', plan.change);
      } else if (plan.additions.length) {
        await postJson('cart/add.js', { items: plan.additions });
      } else {
        return { cart, changed };
      }
      changed = true;
      cart = await getCart();
    }
    throw new Error('Nie udało się dopasować liczby opakowań do produktów.');
  }

  async function reconcile() {
    const wrapping = await reconcileWrapping(await getCart());
    let cart = wrapping.cart;
    let changed = wrapping.changed;
    // Ponowne odczyty uwzględniają zmianę kluczy i rabatów po dodaniu gratisu.
    for (let attempt = 0; attempt < 6; attempt++) {
      const wantedId = wantedGift(cart);
      const updates = {};
      let found = false;
      for (const item of cart.items.filter(isGift)) {
        if (Number(item.variant_id) !== wantedId || found) {
          updates[item.key] = 0;
        } else {
          found = true;
          if (item.quantity !== 1) updates[item.key] = 1;
        }
      }
      if (Object.keys(updates).length) {
        await postJson('cart/update.js', { updates });
      } else if (wantedId && !found) {
        await postJson('cart/add.js', {
          items: [{ id: wantedId, quantity: 1, properties: { _is_gift: 'true' } }]
        });
      } else {
        return { cart, changed };
      }
      changed = true;
      cart = await getCart();
    }
    throw new Error('Nie udało się potwierdzić właściwego gratisu. Spróbuj ponownie.');
  }

  function showError(error, prefix = '') {
    console.error('Koszyk:', error);
    let notice = document.getElementById('gift-system-error');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'gift-system-error';
      notice.className = 'page-width';
      notice.setAttribute('role', 'alert');
      notice.style.cssText = 'padding:16px;margin:16px auto;border:1px solid currentColor;';
    }
    const errorHost = document.querySelector('cart-drawer.active .drawer__footer') ||
      document.getElementById('MainContent') || document.body;
    errorHost.prepend(notice);
    notice.replaceChildren(document.createTextNode(
      `${prefix}Nie udało się przygotować koszyka. ${error.message || 'Spróbuj ponownie.'} `
    ));
    const link = document.createElement('a');
    link.href = `${ROOT}cart`;
    link.textContent = 'Pokaż koszyk';
    notice.appendChild(link);
    notice.hidden = false;
  }

  function clearError() {
    const notice = document.getElementById('gift-system-error');
    if (notice) notice.hidden = true;
  }

  async function fetchSections(ids) {
    const sections = {};
    for (let offset = 0; offset < ids.length; offset += 5) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', ids.slice(offset, offset + 5).join(','));
      Object.assign(sections, await requestJson(url.toString()));
    }
    return sections;
  }

  function notify(cart) {
    document.dispatchEvent(new CustomEvent('gift:updated', { detail: { cart } }));
  }

  // Wspólne odświeżanie po zmianie ilości, wejściu na stronę i powrocie
  // z checkoutu. Widok może wymagać zmiany, nawet gdy gratis jest już poprawny.
  async function refreshView(cart) {
    const focused = document.activeElement;
    const mounts = [
      { id: 'cart-icon-bubble', selector: '#cart-icon-bubble', source: '.shopify-section' }
    ];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const element = document.getElementById(id);
      if (element) {
        mounts.push({ id: element.dataset.id, selector: `#${id} .js-contents`, source: '.js-contents' });
        element.classList.toggle('is-empty', cart.item_count === 0);
      }
    }
    const drawer = document.querySelector('cart-drawer');
    if (drawer) mounts.push({ id: 'cart-drawer', selector: '#CartDrawer', source: '#CartDrawer' });
    let sections = {};
    try {
      sections = await fetchSections([...new Set(mounts.map(mount => mount.id))]);
    } catch (error) {
      console.error('Pobranie sekcji koszyka:', error);
    }
    const replacements = mounts.map(mount => ({
      mount,
      source: sections[mount.id]
        ? new DOMParser().parseFromString(sections[mount.id], 'text/html').querySelector(mount.source)
        : null
    }));
    // Shopify może zwrócić null dla sekcji. Wtedy pobieramy HTML koszyka
    // i podmieniamy jego fragmenty bez przeładowywania karty przeglądarki.
    let fallbackDocument;
    if (replacements.some(({ mount, source }) => !source && document.querySelector(mount.selector))) {
      const url = new URL(window.location.href);
      url.pathname = `${ROOT}cart`;
      url.searchParams.delete('sections');
      url.searchParams.delete('section_id');
      const response = await nativeFetch(url.toString(), { cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Nie udało się odświeżyć widoku koszyka.');
      fallbackDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
    }
    for (const { mount, source } of replacements) {
      const target = document.querySelector(mount.selector);
      const content = source || fallbackDocument?.querySelector(mount.selector);
      if (target && content && target.innerHTML !== content.innerHTML) target.innerHTML = content.innerHTML;
    }
    document.querySelectorAll('cart-items, cart-drawer').forEach(element => {
      element.classList.toggle('is-empty', cart.item_count === 0);
    });
    if (drawer) drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    if (focused && !focused.isConnected) {
      const quantities = Array.from(document.querySelectorAll('.quantity__input'));
      const replacement = focused.dataset?.quantityLineKey
        ? quantities.find(input => input.dataset.quantityLineKey === focused.dataset.quantityLineKey) ||
          quantities.find(input => input.dataset.quantityVariantId === focused.dataset.quantityVariantId)
        : focused.id ? document.getElementById(focused.id) : null;
      replacement?.focus({ preventScroll: true });
    }
    updateWrappingControls();
  }

  function updateWrappingControls() {
    document.querySelectorAll('cart-items, cart-drawer-items').forEach(element => {
      element.toggleAttribute('data-gift-wrap-busy', wrappingBusy);
    });
    document.querySelectorAll('[data-gift-wrap]').forEach(input => {
      const disabled = wrappingBusy || checkingOut || input.dataset.wrapUnavailable === 'true';
      if (input.disabled !== disabled) input.disabled = disabled;
    });
    document.querySelectorAll('cart-items .cart-item, cart-drawer-items .cart-item').forEach(row => {
      if (row.inert !== wrappingBusy) row.inert = wrappingBusy;
    });
  }

  function findWrappingParent(cart, selection) {
    return cart.items.find(item => canWrap(item) && item.key === selection.key) ||
      cart.items.find(item => canWrap(item) && selection.wrapId && item.properties?._gift_wrap_id === selection.wrapId) ||
      cart.items.find(item => canWrap(item) && Number(item.variant_id) === Number(selection.variantId) &&
        String(item.selling_plan_allocation?.selling_plan?.id || '') === selection.sellingPlanId &&
        propertiesKey(item.properties) === propertiesKey(selection.properties));
  }

  function wrappingProperties(previous, wrapId, enabled) {
    // Keep a stable ID: Shopify cannot clear all properties with an empty object.
    const properties = { ...previous, _gift_wrap_id: wrapId };
    delete properties._gift_wrap_selected;
    delete properties[WRAP_LABEL];
    if (enabled) {
      properties._gift_wrap_selected = 'true';
      properties[WRAP_LABEL] = 'Tak';
    }
    return properties;
  }

  function setGiftWrap(selection, enabled) {
    return enqueue(async () => {
      let cart = await getCart();
      const parent = findWrappingParent(cart, selection);
      if (!parent) throw new Error('Produkt zmienił się w koszyku. Odśwież koszyk i spróbuj ponownie.');
      const wrapId = parent.properties?._gift_wrap_id ||
        window.crypto?.randomUUID?.() || `wrap-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      selection.wrapId = wrapId;
      const previous = parent.properties || {};
      try {
        await postJson('cart/change.js', {
          id: parent.key,
          quantity: parent.quantity,
          properties: wrappingProperties(previous, wrapId, enabled)
        });
        cart = (await reconcile()).cart;
      } catch (error) {
        // Undo a failed opt-in (e.g. sold-out packaging), preserving other properties.
        // Keep the ID even when off: Shopify cannot clear properties with {}.
        if (enabled && previous._gift_wrap_selected !== 'true') {
          try {
            cart = await getCart();
            const limit = cart.items.length + 1;
            for (let attempt = 0; attempt < limit; attempt++) {
              const item = cart.items.find(item => canWrap(item) && item.properties?._gift_wrap_id === wrapId &&
                item.properties?._gift_wrap_selected === 'true');
              if (!item) break;
              await postJson('cart/change.js', {
                id: item.key,
                quantity: item.quantity,
                properties: wrappingProperties(item.properties, wrapId, false)
              });
              cart = await getCart();
            }
            await reconcile();
          } catch (rollbackError) {
            console.error('Przywrócenie opcji pakowania:', rollbackError);
          }
        }
        throw error;
      }
      clearError();
      await refreshView(cart);
      notify(cart);
      return cart;
    });
  }

  document.addEventListener('change', async event => {
    const input = event.target.closest?.('[data-gift-wrap]');
    if (!input) return;
    event.stopImmediatePropagation();
    if (wrappingBusy || checkingOut) return;
    const selection = {
      key: input.dataset.lineKey,
      wrapId: input.dataset.wrapId,
      variantId: input.dataset.variantId,
      sellingPlanId: input.dataset.sellingPlanId || '',
      properties: JSON.parse(input.dataset.lineProperties || '{}')
    };
    const focusId = input.id;
    const enabled = input.checked;
    wrappingBusy = true;
    updateWrappingControls();
    input.closest('.cart-gift-wrap').querySelector('[role="status"]').textContent = 'Aktualizowanie pakowania…';
    try {
      await setGiftWrap(selection, enabled);
    } catch (error) {
      input.checked = !enabled;
      try {
        await enqueue(async () => {
          const cart = await getCart();
          await refreshView(cart);
          notify(cart);
        });
      } catch (refreshError) {
        console.error('Odświeżenie pakowania:', refreshError);
      }
      showError(error, 'Nie udało się zmienić pakowania na prezent. ');
    } finally {
      wrappingBusy = false;
      updateWrappingControls();
      const current = document.getElementById(focusId);
      // Prefer the stable ID: adding the packaging can reorder cart line indexes.
      const replacement = Array.from(document.querySelectorAll('[data-gift-wrap]')).find(element =>
        element.id.startsWith(focusId.startsWith('GiftWrap-drawer-') ? 'GiftWrap-drawer-' : 'GiftWrap-cart-') &&
        (element.dataset.lineKey === selection.key ||
          (selection.wrapId && element.dataset.wrapId === selection.wrapId))) || current;
      replacement?.focus({ preventScroll: true });
      document.querySelectorAll('.cart-gift-wrap__status').forEach(status => { status.textContent = ''; });
    }
  }, true);

  document.addEventListener('click', event => {
    if (!wrappingBusy || !event.target.closest?.('.quantity__button, cart-remove-button')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  async function sync(options = {}) {
    return enqueue(async () => {
      const result = await reconcile();
      clearError();
      if ((result.changed || options.forceRefresh) && options.refresh !== false) await refreshView(result.cart);
      notify(result.cart);
      return result.cart;
    });
  }

  function mutationType(resource, options) {
    try {
      const url = new URL(typeof resource === 'string' || resource instanceof URL ? resource : resource.url, window.location.origin);
      const method = String(options?.method || resource?.method || 'GET').toUpperCase();
      if (url.origin !== window.location.origin || method !== 'POST') return null;
      return url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/)?.[1] || null;
    } catch {
      return null;
    }
  }

  function propertiesKey(properties) {
    return JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  }

  function sameLine(left, right) {
    return Number(left.variant_id || left.id) === Number(right.variant_id || right.id) &&
      propertiesKey(left.properties) === propertiesKey(right.properties) &&
      left.selling_plan_allocation?.selling_plan?.id === right.selling_plan_allocation?.selling_plan?.id;
  }

  window.fetch = function (resource, options) {
    const type = mutationType(resource, options);
    if (!type) return nativeFetch(resource, options);
    return enqueue(async () => {
      const response = await nativeFetch(resource, options);
      if (!response.ok) return response;
      const original = await response.clone().json().catch(() => null);
      if (original?.errors || Number(original?.status) >= 400) return response;
      let result;
      try {
        result = await reconcile();
        clearError();
      } catch (error) {
        // Produkt został już dodany. Zachowujemy powodzenie tej operacji,
        // aby błąd gratisu nie spowodował ponownego dodania produktu.
        showError(error, 'Koszyk został zmieniony. ');
        return response;
      }
      notify(result.cart);
      if (!original) return response;
      let data = { ...original };
      if (type !== 'add') {
        data = { ...data, ...result.cart };
      } else if (original.items) {
        data.items = original.items.map(item => result.cart.items.find(line => sameLine(line, item)) || item);
      } else {
        // Rabaty mogą zmienić klucz pozycji używany w okienku potwierdzenia.
        data = { ...data, ...result.cart.items.find(line => sameLine(line, original)) };
      }
      if (original.sections && result.changed) {
        try {
          const updated = await fetchSections(Object.keys(original.sections));
          data.sections = { ...original.sections };
          for (const [id, html] of Object.entries(updated)) {
            if (html !== null) data.sections[id] = html;
          }
        } catch (error) {
          console.error('Odświeżenie widoku koszyka:', error);
        }
      }
      const headers = new Headers(response.headers);
      headers.delete('content-length');
      headers.delete('content-encoding');
      headers.set('Content-Type', 'application/json');
      return new Response(JSON.stringify(data), { status: response.status, statusText: response.statusText, headers });
    });
  };

  function updateBuyButtons() {
    updateWrappingControls();
    document.querySelectorAll('[data-gift-buy-now]').forEach(button => {
      const form = button.closest('form');
      const addButton = form?.querySelector('[name="add"]');
      const id = form?.querySelector('[name="id"]');
      const disabled = checkingOut || !id?.value || id.disabled || !addButton || addButton.disabled ||
        addButton.getAttribute('aria-disabled') === 'true';
      if (button.disabled !== Boolean(disabled)) button.disabled = Boolean(disabled);
    });
  }

  function formSignature(data) {
    return JSON.stringify(Array.from(data.entries(), ([key, value]) => [key,
      typeof value === 'string' ? value : [value.name, value.size, value.lastModified]
    ]));
  }

  function assertCheckout(cart) {
    if (!cart.items.some(canWrap)) throw new Error('Koszyk jest pusty.');
    const wrapping = wrappingPlan(cart);
    if (wrapping.additions.length || wrapping.change || Object.keys(wrapping.removals).length ||
        cart.items.some(item => canWrap(item) && item.properties?._gift_wrap_selected === 'true' &&
          !item.properties?._gift_wrap_id)) {
      throw new Error('Pakowanie na prezent nie zgadza się z zawartością koszyka. Odśwież koszyk i spróbuj ponownie.');
    }
    if (cart.items.some(item => isGift(item) && item.final_line_price > 0)) {
      throw new Error('Gratis ma cenę większą od zera. Sprawdź cenę wariantu lub rabat w Shopify.');
    }
  }

  async function goToCheckout(button, productForm) {
    if (checkingOut || wrappingBusy || button?.disabled) return;
    if (productForm && !productForm.reportValidity()) return;
    const formData = productForm ? new FormData(productForm) : null;
    if (formData) {
      formData.delete('sections');
      formData.delete('sections_url');
      if (!formData.get('id')) return;
    }
    checkingOut = true;
    const previousText = button?.textContent;
    const previousDisabled = button?.disabled;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (button.matches('[data-gift-buy-now]')) button.textContent = 'Przekierowanie…';
    }
    updateBuyButtons();
    try {
      await enqueue(async () => {
        if (productForm) {
          const signature = formSignature(formData);
          const pending = pendingPurchases.get(productForm);
          const cart = pending?.signature === signature ? await getCart() : null;
          const alreadyAdded = cart?.items.some(item => sameLine(item, pending.item) && item.quantity >= pending.item.quantity);
          if (!alreadyAdded) {
            const added = await requestJson(`${ROOT}cart/add.js`, {
              method: 'POST', body: formData, headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });
            pendingPurchases.set(productForm, { signature, item: added.items?.[0] || added });
          }
        }
        const { cart } = await reconcile();
        assertCheckout(cart);
        clearError();
        window.location.assign(`${ROOT}checkout`);
      });
    } catch (error) {
      showError(error, productForm && pendingPurchases.has(productForm) ? 'Produkt jest już w koszyku. ' : '');
    } finally {
      checkingOut = false;
      if (button) {
        button.disabled = previousDisabled;
        button.removeAttribute('aria-busy');
        if (button.matches('[data-gift-buy-now]')) button.textContent = previousText;
      }
      updateBuyButtons();
    }
  }

  function checkoutLink(target) {
    const element = target?.closest('button[name="checkout"], input[name="checkout"], a[href]');
    if (!element || element.tagName !== 'A') return element;
    try {
      const url = new URL(element.href, window.location.origin);
      return url.origin === window.location.origin && /\/checkout\/?$/.test(url.pathname) ? element : null;
    } catch {
      return null;
    }
  }

  document.addEventListener('click', event => {
    const buyNow = event.target.closest?.('[data-gift-buy-now]');
    const button = buyNow || checkoutLink(event.target);
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    goToCheckout(button, buyNow ? buyNow.closest('form') : null);
  }, true);

  document.addEventListener('submit', event => {
    if (checkingOut && event.target.matches('form[action*="/cart"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (event.submitter?.name !== 'checkout' || !event.target.matches('form[action*="/cart"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    goToCheckout(event.submitter, null);
  }, true);

  function start() {
    updateBuyButtons();
    new MutationObserver(updateBuyButtons).observe(document.body, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'value']
    });
    sync().catch(error => showError(error));
  }

  window.GiftSystem = { sync, getCart: () => enqueue(getCart) };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('pageshow', event => {
    if (event.persisted) sync().catch(error => showError(error));
  });
})();
